import { Injectable } from '@angular/core';

import { Constants } from '@utils/constants';
import { ISelectOptions } from '@models/form/select';
import { IFileConfig, IValidationMessage } from '@models/file';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  static validateFileExtension(
    fileName: string,
    allowedExtensionList: string[] = [],
  ): boolean {
    const extension = this.getFileExtension(fileName);
    const hasValidFileExtension =
      !!allowedExtensionList &&
      allowedExtensionList.some((extensionType) => extensionType === extension);

    return hasValidFileExtension;
  }

  static validateFileMimeType(
    fileMimeType: string,
    allowedMimeList: string[] = [],
  ): boolean {
    const allowedTypes = allowedMimeList.map((mimeType) => mimeType.split('/')[0]);
    return !!allowedMimeList && allowedTypes.some((allowedType) => fileMimeType.startsWith(allowedType));
  }

  static validateFileSize(size: number): boolean {
    const isFileSizeValid = !!size && size < Constants.MAXFILESIZE;

    return isFileSizeValid;
  }

  static validateFileNameLength(filename: string): boolean {
    const isFileNameValid = filename?.length < Constants.MAXFILENAMELENGTH;

    return isFileNameValid;
  }

  static getFileExtension(fileName: string): string {
    if (!!fileName && fileName.includes('.')) {
      return fileName.split('.').pop().toLowerCase();
    } else {
      throw new Error('File name is missing extension.');
    }
  }

  /**
   * Convert the provided `files` into a `FormData` object that can be uploaded.
   */
  public buildFormData(
    files: FileList,
    props?: ISelectOptions[],
    filePropName: string = 'files',
  ): FormData {
    const fileListFormData: FormData = new FormData();
    if (props?.length) {
      props.forEach((prop) =>
        this.appendToFormData(prop.name, prop.value, fileListFormData),
      );
    }
    // FileList does not have access to Array methods like `forEach()`
    /* tslint:disable:prefer-for-of */
    if (files?.length > 0) {
      for (let i: number = 0; i < files.length; i++) {
        this.appendToFormData(filePropName, files[i], fileListFormData);
      }
    }

    return fileListFormData;
  }

  private appendToFormData(
    propertyName: string,
    value: string | Blob,
    formData: FormData,
  ): void {
    formData.append(propertyName, value);
  }

  public buildValidationSuccessMessageList(
    fileList: FileList,
    fileConfig: IFileConfig,
  ): IValidationMessage[] {
    const validationSuccessMessageList: IValidationMessage[] = [];
    // FileList does not have access to Array methods like `forEach()`
    for (let i = 0; i < fileList?.length; i++) {
      if (
        FileService.validateFileExtension(
          fileList[i].name,
          fileConfig.allowedFileExtensionList,
        ) &&
        FileService.validateFileSize(fileList[i].size) &&
        FileService.validateFileNameLength(fileList[i].name) &&
        FileService.validateFileMimeType(
          fileList[i].type,
          fileConfig.allowedMimeTypeList,
        )
      ) {
        const validationMessage = {
          extensionIcon: this.setExtensionIcon(fileList[i].name),
          message: `${fileList[i].name} can be uploaded.`,
        };
        validationSuccessMessageList.push(validationMessage);
      }
    }

    return validationSuccessMessageList;
  }

  public setExtensionIcon(fileName: string): string {
    const fileType = FileService.getFileExtension(fileName).toLowerCase();
    let iconName = '';

    switch (fileType) {
      case 'pdf':
        iconName = 'picture_as_pdf';
        break;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        iconName = 'image';
        break;
      case 'mp3':
      case 'wav':
      case 'flac':
      case 'mp4':
        iconName = 'headphones';
        break;
      default:
        iconName = 'description';
    }

    return iconName;
  }
}
